import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarPlus, ChevronDown, ChevronUp, Pencil } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtFecha, subtituloVacaciones } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import CorreccionFichajeDialog from "@/components/CorreccionFichajeDialog";
import JustificarIncidenciaDialog from "@/components/JustificarIncidenciaDialog";

function Tarjeta({ titulo, valor, subtitulo, color, detalle, renderAccion }) {
  const [open, setOpen] = useState(false);
  const tieneDetalle = detalle && detalle.length > 0;
  return (
    <div className={`rounded-xl border bg-white shadow-sm ${color}`}>
      <button
        type="button"
        className="flex w-full items-center justify-between gap-3 p-4 text-left"
        onClick={() => tieneDetalle && setOpen(!open)}
        disabled={!tieneDetalle}
      >
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">{titulo}</p>
          <p className="mt-1 font-display text-2xl font-semibold text-zinc-900">{valor}</p>
          {subtitulo && <p className="mt-0.5 text-xs text-zinc-500">{subtitulo}</p>}
        </div>
        {tieneDetalle && (
          <span className="text-zinc-400">
            {open ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </span>
        )}
      </button>
      {open && tieneDetalle && (
        <div className="border-t border-zinc-100 px-4 pb-4 pt-2">
          <ul className="space-y-2 text-sm">
            {detalle.map((item, i) => (
              <li key={item.id || item.fecha || i} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-zinc-50 px-3 py-2">
                <span className="text-zinc-700">
                  {item.fecha_inicio
                    ? `${fmtFecha(item.fecha_inicio)} — ${fmtFecha(item.fecha_fin)}`
                    : fmtFecha(item.fecha)}
                  {item.hora ? ` · ${item.hora}` : ""}
                </span>
                <span className="flex items-center gap-2 text-xs text-zinc-500">
                  {item.dias != null && <span>{item.dias} d</span>}
                  {item.estado && (
                    <Badge variant="outline" className="text-[10px]">{item.estado}</Badge>
                  )}
                  {renderAccion?.(item)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function MiCuadro() {
  const [data, setData] = useState(null);
  const [correccionItem, setCorreccionItem] = useState(null);
  const [justificarItem, setJustificarItem] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const { data: d } = await api.get("/trabajador/mi-cuadro");
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  if (!data) {
    return (
      <div className="flex justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-600" />
      </div>
    );
  }

  const v = data.vacaciones || {};
  const vacSub = subtituloVacaciones({
    dias_anuales: v.dias_anuales,
    meses_trabajados: v.meses_trabajados,
    proporcional: v.proporcional,
    modo_label: data.vacaciones_modo_label,
  });

  const btnCorregir = (item) => (
    <span className="flex flex-wrap items-center gap-1">
      <Button size="sm" variant="ghost" className="h-7 gap-1 text-indigo-600" onClick={() => setCorreccionItem(item)}>
        <Pencil className="h-3 w-3" /> Corregir hora
      </Button>
      {item.fecha && (
        <Button size="sm" variant="ghost" className="h-7 gap-1 text-emerald-700" asChild>
          <Link
            to={`/mis-solicitudes?nueva=ausencia_justificada&alcance=llegada_tardia&fecha=${encodeURIComponent(item.fecha)}`}
            data-testid="retraso-justificar-ausencia-link"
          >
            <CalendarPlus className="h-3 w-3" /> Justificar hueco
          </Link>
        </Button>
      )}
    </span>
  );

  const btnJustificar = (item) => (
    <Button size="sm" variant="ghost" className="h-7 gap-1 text-rose-600" onClick={() => setJustificarItem(item)}>
      <Pencil className="h-3 w-3" /> Justificar
    </Button>
  );

  return (
    <div className="space-y-5" data-testid="mi-cuadro-page">
      <div className="rounded-2xl border border-indigo-100 bg-[#eef0fa] p-4">
        <h1 className="font-display text-2xl font-semibold text-zinc-900">Cuadro</h1>
        <p className="mt-1 text-sm text-indigo-700/80">
          Resumen {data.anio} · {vacSub}
        </p>
      </div>

      <Tarjeta
        titulo="Vacaciones anuales disponibles"
        valor={`${v.restantes ?? 0} / ${v.disponibles ?? 0}`}
        subtitulo={
          Number(v.arrastre) !== 0
            ? `${v.usados ?? 0} usados · ${v.del_anio ?? "—"} del año · ${v.arrastre > 0 ? "+" : ""}${v.arrastre} arrastre · ${vacSub}`
            : `${v.usados ?? 0} días usados · ${vacSub}`
        }
        color="border-emerald-100"
        detalle={v.asignadas}
      />

      <Tarjeta
        titulo="Vacaciones asignadas"
        valor={`${v.dias_asignados ?? 0} d`}
        subtitulo={
          (v.asignados_pendientes ?? 0) > 0
            ? `Solo periodos aprobados · ${v.asignados_pendientes} d pendientes de confirmar o revisar`
            : "Solo periodos aprobados y confirmados"
        }
        color="border-sky-100"
        detalle={v.asignadas}
      />

      <Tarjeta
        titulo="Incapacidad temporal"
        valor={`${data.incapacidad_temporal?.dias ?? 0} d`}
        subtitulo="Bajas aprobadas"
        color="border-amber-100"
        detalle={data.incapacidad_temporal?.detalle}
      />

      <Tarjeta
        titulo="Permisos justificados"
        valor={`${data.permisos_justificados?.dias ?? 0} d`}
        subtitulo="Asuntos propios y fuera de horario justificado"
        color="border-violet-100"
        detalle={data.permisos_justificados?.detalle}
      />

      <Tarjeta
        titulo="Incidencias no justificadas"
        valor={data.incidencias_no_justificadas?.total ?? 0}
        subtitulo="Fuera de horario sin justificar"
        color="border-rose-100"
        detalle={data.incidencias_no_justificadas?.detalle}
        renderAccion={btnJustificar}
      />

      <Tarjeta
        titulo="Retrasos"
        valor={data.retrasos?.total ?? 0}
        subtitulo="Entradas fuera del tiempo de cortesía (sin ausencia aprobada)"
        color="border-orange-100"
        detalle={data.retrasos?.detalle}
        renderAccion={(item) => item.fichaje_id ? btnCorregir(item) : null}
      />

      <CorreccionFichajeDialog item={correccionItem} onClose={() => setCorreccionItem(null)} onSuccess={cargar} />
      <JustificarIncidenciaDialog item={justificarItem} onClose={() => setJustificarItem(null)} onSuccess={cargar} />
    </div>
  );
}
